import { BookingResponseDto } from "../../api/bookingApi.ts";
import { useLanguageStore } from "../../stores/languageStore.ts";
import {
  getInstrumentIcon,
  getTranslations,
  useSupportedInstruments,
} from "../../api/instrumentApi.ts";
import { Calendar, CreditCard, Euro } from "lucide-react";
import { Trans } from "@lingui/react/macro";
import { Link } from "react-router";
import { ProfilePicture } from "../profile/ProfilePicture.tsx";
import { format } from "date-fns";
import { de, enUS } from "date-fns/locale";

interface UserPendingBookingCardProps {
  booking: BookingResponseDto;
}

export default function UserPendingBookingCard({
  booking,
}: UserPendingBookingCardProps) {
  const { language } = useLanguageStore();
  const { data: supportedInstruments } = useSupportedInstruments();
  const InstrumentIcon = getInstrumentIcon(booking.instrument);
  const instrumentLabel = getTranslations(
    supportedInstruments,
    booking.instrument,
    language,
  );

  const locale = language === "de" ? de : enUS;
  const firstSlot = booking.timeSlots[0];
  const slotDisplay = firstSlot
    ? `${format(new Date(firstSlot.from), "EEE MMM d, HH:mm", { locale })} – ${format(new Date(firstSlot.to), "HH:mm", { locale })}`
    : null;

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4 shadow-[--shadow-card]">
      {/* Header: instrument + awaiting payment badge */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <InstrumentIcon className="h-5 w-5 shrink-0 text-blue-700" />
          <span className="truncate text-sm font-semibold text-blue-900">
            {instrumentLabel}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
          <CreditCard className="h-3 w-3" />
          <Trans>Awaiting Payment</Trans>
        </div>
      </div>

      {/* Teacher info */}
      {booking.teacherName && (
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 shrink-0">
            <ProfilePicture
              profilePictureUrl={booking.teacherProfilePictureUrl}
              className="h-9 w-9"
              iconSize={20}
            />
          </div>
          <Link
            to={`/profile/${booking.teacherId}`}
            className="truncate text-sm font-medium text-blue-900 hover:underline"
          >
            {booking.teacherName}
          </Link>
        </div>
      )}

      {/* First time slot */}
      {slotDisplay && (
        <div className="flex items-center gap-1.5 text-xs font-medium text-blue-900">
          <Calendar className="h-3.5 w-3.5 shrink-0 opacity-60" />
          <span>{slotDisplay}</span>
        </div>
      )}

      {/* Price */}
      {booking.price !== undefined && (
        <div className="flex items-center gap-1.5 text-xs font-medium text-blue-900">
          <Euro className="h-3.5 w-3.5 shrink-0 opacity-60" />
          <span>{booking.price.toFixed(2)}</span>
        </div>
      )}

      {/* Pay action */}
      <Link
        to={`/checkout/${booking.id}`}
        className="mt-1 flex w-full items-center justify-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2.5 text-sm font-medium text-white shadow-sm transition-all duration-150 hover:bg-blue-700 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:outline-none active:scale-[0.98]"
      >
        <CreditCard className="h-4 w-4" />
        <span>
          <Trans>Complete Payment</Trans>
        </span>
      </Link>
    </div>
  );
}
